<script lang="ts">
  import { Link, navigate } from 'svelte-routing';
  import { api, ApiError } from '../lib/api';
  import { clearAuth, type User } from '../lib/stores/auth';
  import { showToast } from '../lib/stores/toast';
  import DonorIcon from './DonorIcon.svelte';

  // Account dropdown anchored to the username button. Used in two shells:
  //
  // 1. TopNav (auth/account/static routes) — compact pill, panel opens
  //    beneath the trigger and right-aligns to it.
  // 2. Sidebar footer (game routes) — wide row matching the nav items,
  //    panel opens ABOVE the trigger (no room beneath the bottom rail)
  //    and left-aligns to the rail.
  //
  // Both shells share the same menu items so the player learns one
  // affordance regardless of which surface they're on.

  type Placement = 'bottom-end' | 'top-start' | 'top-stretch';
  type TriggerVariant = 'compact' | 'wide' | 'pill';

  interface Props {
    user: User;
    /**
     * Where the panel sits relative to the trigger.
     *  - `bottom-end` (default): below the trigger, right-aligned —
     *    TopNav.
     *  - `top-start`: above the trigger, left-aligned to the trigger's
     *    left edge with auto-width content (min 200 px) — used by the
     *    mobile auth pill in AppLayout, which sits in the bottom-left
     *    corner so it has to open upward AND right.
     *  - `top-stretch`: above the trigger, stretched to fill the
     *    user-menu container's width — used by the sidebar footer's
     *    wide row so the popover spans the rail's gutter.
     */
    placement?: Placement;
    /**
     * Trigger styling.
     *  - `compact` (default): TopNav rounded-rect pill.
     *  - `wide`: full-width row that matches sidebar nav items so the
     *    auth row blends into the rail visually.
     *  - `pill`: small floating chip matching the mobile hamburger's
     *    chrome (dark bg + blur + border + small radius). Used by the
     *    bottom-left mobile auth pill.
     */
    triggerVariant?: TriggerVariant;
    /**
     * Whether to show the username text alongside the donor icon. The
     * sidebar hides this when collapsed (icon-only rail) via its own
     * label rules, so callers don't usually need to override — but it's
     * exposed in case a future shell wants an icon-only trigger that
     * still says "click me, I'm an account menu" via the donor icon.
     */
    showUsername?: boolean;
  }

  let {
    user: u,
    placement = 'bottom-end',
    triggerVariant = 'compact',
    showUsername = true,
  }: Props = $props();

  let open = $state(false);
  let menuRef = $state<HTMLDivElement | null>(null);
  let signingOut = $state(false);

  // Shorthand for the public profile + tab-deep-links so the markup stays
  // legible. /account?tab=stats is read by Account/Index.svelte's
  // readInitialTab() so the menu lands on the right tab on a fresh nav.
  const profileHref = $derived(`/u/${u.username}`);
  const ACCOUNT_HREF = '/account';
  const STATS_HREF = '/account?tab=stats';
  const SETTINGS_HREF = '/account?tab=settings';

  // Donor-icon trigger size, scaled by variant.
  //  - wide (sidebar): 28 — the rail row has space and a bigger glyph
  //    reads better against the surrounding nav metrics.
  //  - pill (mobile): 18 — the floating chip is small chrome; matches
  //    the inline user-icon size of the logged-out Login link.
  //  - compact (TopNav): 24 — the existing default.
  const iconSize = $derived(
    triggerVariant === 'wide' ? 28 : triggerVariant === 'pill' ? 18 : 24,
  );

  function toggle(): void {
    open = !open;
  }

  function close(): void {
    open = false;
  }

  // Close on outside click / Escape so the menu stays minimally intrusive.
  $effect(() => {
    if (!open) return;

    const handleClick = (e: MouseEvent): void => {
      if (menuRef && !menuRef.contains(e.target as Node)) close();
    };
    const handleKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') close();
    };

    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  });

  async function signOut(): Promise<void> {
    if (signingOut) return;
    signingOut = true;
    try {
      await api('/api/auth/logout', { method: 'POST' });
      clearAuth();
      close();
      navigate('/');
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Sign out failed.';
      showToast(msg, 'error');
    } finally {
      signingOut = false;
    }
  }
</script>

<div
  class="user-menu user-menu--placement-{placement} user-menu--trigger-{triggerVariant}"
  bind:this={menuRef}
>
  <button
    type="button"
    class="user-menu__trigger"
    aria-haspopup="menu"
    aria-expanded={open}
    aria-label={`Account menu — signed in as ${u.username}`}
    onclick={toggle}
  >
    <DonorIcon tier={u.donor_tier} size={iconSize} />
    {#if showUsername}
      <span class="user-menu__username">{u.username}</span>
    {/if}
    <svg class="user-menu__chevron" viewBox="0 0 24 24" aria-hidden="true">
      <polyline points="6,9 12,15 18,9" />
    </svg>
  </button>

  {#if open}
    <div class="user-menu__panel" role="menu">
      <Link
        to={ACCOUNT_HREF}
        getProps={() => ({ class: 'user-menu__item', role: 'menuitem' })}
      >
        <span class="user-menu__item-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <circle cx="12" cy="9" r="3.5" />
            <path d="M5 20 C5 16 8 14 12 14 S19 16 19 20" />
          </svg>
        </span>
        Account
      </Link>
      <Link
        to={profileHref}
        getProps={() => ({ class: 'user-menu__item', role: 'menuitem' })}
      >
        <span class="user-menu__item-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M2 12 C5 7 8 5 12 5 S19 7 22 12 C19 17 16 19 12 19 S5 17 2 12 Z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        </span>
        View profile
      </Link>
      <Link
        to={STATS_HREF}
        getProps={() => ({ class: 'user-menu__item', role: 'menuitem' })}
      >
        <span class="user-menu__item-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round">
            <path d="M5 19 L5 12" />
            <path d="M12 19 L12 7" />
            <path d="M19 19 L19 14" />
            <path d="M3 21 L21 21" />
          </svg>
        </span>
        Stats
      </Link>
      <Link
        to={SETTINGS_HREF}
        getProps={() => ({ class: 'user-menu__item', role: 'menuitem' })}
      >
        <span class="user-menu__item-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round">
            <circle cx="12" cy="12" r="2.5" />
            <path d="M12 3 L13 6 L16 5 L17 8 L20 9 L19 12 L20 15 L17 16 L16 19 L13 18 L12 21 L11 18 L8 19 L7 16 L4 15 L5 12 L4 9 L7 8 L8 5 L11 6 Z" />
          </svg>
        </span>
        Settings
      </Link>
      <div class="user-menu__divider" role="separator"></div>
      <button
        type="button"
        class="user-menu__item"
        role="menuitem"
        disabled={signingOut}
        onclick={signOut}
      >
        <span class="user-menu__item-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M9 5 L5 5 L5 19 L9 19" />
            <path d="M14 8 L18 12 L14 16" />
            <path d="M9 12 L18 12" />
          </svg>
        </span>
        {signingOut ? 'Signing out…' : 'Log out'}
      </button>
    </div>
  {/if}
</div>
